import {IconButton} from '@mui/material';
import { Icon } from '@mui/material';
import { Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {useDate} from '../contexts/DateContext';
import { useSimulation } from '../contexts/SimulationContext';
import { useTimezone } from "../contexts/TimezoneContext";
import dayjs from 'dayjs';
import {useStrategy} from "../api/strategy.ts";

export default function CustomDatePicker() {
    const { selectedDate, setSelectedDate } = useDate();
  const { simulationParams, setSimulationParams } = useSimulation();
  const { selectedTimezone } = useTimezone();

  const { isLoading: isStrategyLoading } = useStrategy();

  const updateDateAndSimulation = (newDate: dayjs.Dayjs) => {

    if (isStrategyLoading) return;
    const nextDate = newDate.tz(selectedTimezone);
    // Update the date context
    setSelectedDate(nextDate);

    // Also update the simulationParams with the new date
    if (simulationParams) {
      const isoWithOffset = nextDate.toISOString();

      setSimulationParams({
          ...simulationParams,
          startTime: isoWithOffset,
          timezone: selectedTimezone,
      });
    }
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    if (!newValue) return;
    updateDateAndSimulation(newValue);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton onClick={() => updateDateAndSimulation(selectedDate.add(-1, 'day'))}
                  disabled={isStrategyLoading}
                  sx={{color:'white',
                       "&:focus": {
                           outline: "none", // Remove the focus outline/border
                           boxShadow: "none", // Remove any focus shadow
                       },
                      // Ensure the arrow buttons also look disabled
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      }
                  }}>
        <Icon>keyboard_arrow_left</Icon>
      </IconButton>
      <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs} adapterLocale="en">
        <DatePicker
          key={`picker-${selectedTimezone}-${selectedDate.format('YYYY-MM-DD')}`}
          value={selectedDate} // Use the state variable
          format="D MMM YYYY" // The key change: format prop
          onAccept={handleDateChange}
          onChange={() => {}}
          disabled={isStrategyLoading}
          sx={{
            // 1. Control the overall width of the picker container
            maxWidth: '160px',
            '& .MuiInputBase-root': {
              paddingRight: '4px', // Reduce space before the calendar icon
            },
            '& .MuiInputBase-input': {
              color: 'white', // Date text color
              "&.Mui-disabled": {
                WebkitTextFillColor: "rgba(255, 255, 255, 0.3)",
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent', // Border color
              border: 'none',
            },
            '& .MuiInputLabel-root': {
              color: 'white', // Label color
            },
            '& .MuiSvgIcon-root': {
              fill: 'white', // Calendar icon color
            },
            '& .MuiButtonBase-root': {
              color: 'white', // Arrow icon color
              "&:focus": {
                  outline: "none", // Remove the focus outline/border
                  boxShadow: "none", // Remove any focus shadow
              },
              // This targets the Calendar Icon button specifically
              "&.Mui-disabled": {
                color: "rgba(255, 255, 255, 0.3)",
                "& .MuiSvgIcon-root": {
                  fill: "rgba(255, 255, 255, 0.3)", // Force icon color change
                }
              },
            },
            '& .MuiPickersDay-root': {
              color: 'white',
              '&.Mui-selected': {
                backgroundColor: 'white',
                color: 'black',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
              '&:hover': {
                backgroundColor: 'transparent',
                border: 'none',
              },
            },
          }}
        />
      </LocalizationProvider>
      <IconButton onClick={() => updateDateAndSimulation(selectedDate.add(1, 'day'))}
                  disabled={isStrategyLoading}
                  sx={{color:'white',
                       "&:focus": {
                           outline: "none", // Remove the focus outline/border
                           boxShadow: "none", // Remove any focus shadow
                       },
                      // Ensure the arrow buttons also look disabled
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      }
                  }}>
        <Icon>keyboard_arrow_right</Icon>
      </IconButton>
    </Box>
  );
}
